import { useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Wheel } from 'https://cdn.jsdelivr.net/npm/spin-wheel@5.0.2/dist/spin-wheel-esm.js';
import styles from './WheelComponent.module.css';
import dcIconPath from '/dcIcon.svg';
import usdtIconPath from '/usdtIcon.svg';
import PointerIcon from '/Pointer.svg';

const WHEEL_ITEMS = [
  { value: '1.0', type: 'USDT', color: '#81B235' },
  { value: '100', type: 'DC', color: '#7135E0' },
  { value: '0.05', type: 'USDT', color: '#81B235' },
  { value: '50', type: 'DC', color: '#7135E0' },
  { value: '0.5', type: 'USDT', color: '#81B235' },
  { value: '20', type: 'DC', color: '#7135E0' },
  { value: '0.01', type: 'USDT', color: '#81B235' },
  { value: '150', type: 'DC', color: '#7135E0' }
];

const CANVAS_CONFIG = {
  width: 140,
  height: 27,
  iconSize: 25,
  padding: 6,
  mainFont: 'bold 28px Lato',
  subFont: 'bold 18px Lato',
  textColor: '#ffffff',
  scale: 2
};

const WHEEL_CONFIG = {
  duration: 4000,
  minRevolutions: 5,
  maxExtraRevolutions: 3,
  imageRadius: 0.63,
  imageRotation: -90,
  imageScale: 1,
  itemLabelRadius: 10.58,
  rotationResistance: -35
};

const createCurrencyImage = async (label, iconPath, text) => {
  const tempDiv = document.createElement('div');
  tempDiv.style.position = 'absolute';
  tempDiv.style.visibility = 'hidden';
  tempDiv.style.width = `${CANVAS_CONFIG.width}px`;
  tempDiv.style.height = `${CANVAS_CONFIG.height}px`;
  document.body.appendChild(tempDiv);

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', String(CANVAS_CONFIG.width));
  svg.setAttribute('height', String(CANVAS_CONFIG.height));
  svg.setAttribute('viewBox', `0 0 ${CANVAS_CONFIG.width} ${CANVAS_CONFIG.height}`);

  return new Promise((resolve) => {
    fetch(iconPath)
      .then(response => response.text())
      .then(svgContent => {
        const canvas = document.createElement('canvas');
        canvas.width = CANVAS_CONFIG.width * CANVAS_CONFIG.scale;
        canvas.height = CANVAS_CONFIG.height * CANVAS_CONFIG.scale;
        const ctx = canvas.getContext('2d');

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.scale(CANVAS_CONFIG.scale, CANVAS_CONFIG.scale);

        ctx.textBaseline = 'middle';
        ctx.fillStyle = CANVAS_CONFIG.textColor;
        ctx.textRendering = 'optimizeLegibility';
        ctx.letterSpacing = '0.5px';

        ctx.font = CANVAS_CONFIG.mainFont;
        ctx.fillText(
          label, 
          CANVAS_CONFIG.iconSize + CANVAS_CONFIG.padding,
          CANVAS_CONFIG.height / 2
        );

        const labelWidth = ctx.measureText(label).width;
        ctx.font = CANVAS_CONFIG.subFont;
        ctx.fillText(
          text,
          CANVAS_CONFIG.iconSize + CANVAS_CONFIG.padding + labelWidth + CANVAS_CONFIG.padding,
          CANVAS_CONFIG.height / 2
        );

        const iconGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');
        iconGroup.innerHTML = svgContent;
        
        const iconElement = iconGroup.firstElementChild;
        iconElement.setAttribute('width', String(CANVAS_CONFIG.iconSize));
        iconElement.setAttribute('height', String(CANVAS_CONFIG.iconSize));
        iconElement.setAttribute('x', '0');
        iconElement.setAttribute('y', '2');

        svg.appendChild(iconGroup);

        const image = document.createElementNS('http://www.w3.org/2000/svg', 'image');
        image.setAttribute('x', '0');
        image.setAttribute('y', '0');
        image.setAttribute('width', String(CANVAS_CONFIG.width));
        image.setAttribute('height', String(CANVAS_CONFIG.height));
        image.setAttribute('href', canvas.toDataURL('image/png', 1.0));
        svg.appendChild(image);

        const svgString = new XMLSerializer().serializeToString(svg);
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);

        const resultImage = new Image();
        resultImage.onload = () => {
          URL.revokeObjectURL(url);
          document.body.removeChild(tempDiv);
          resolve(resultImage);
        };
        resultImage.src = url;
      });
  });
};

const WheelComponent = forwardRef(({ onSpin, onSpinComplete, onInitialized }, ref) => {
  const wheelContainerRef = useRef(null);
  const wheelInstanceRef = useRef(null);
  const isInitializedRef = useRef(false);

  const createWheelItems = useCallback(async () => {
    try {
      const items = await Promise.all(
        WHEEL_ITEMS.map(async ({ value, type, color }) => {
          const iconPath = type === 'USDT' ? usdtIconPath : dcIconPath;
          const image = await createCurrencyImage(value, iconPath, type);
          return {
            backgroundColor: color,
            labelColor: CANVAS_CONFIG.textColor,
            image,
            imageRadius: WHEEL_CONFIG.imageRadius,
            imageRotation: WHEEL_CONFIG.imageRotation,
            imageScale: WHEEL_CONFIG.imageScale,
          };
        })
      );
      return items;
    } catch (error) {
      // console.error('Ошибка создания элементов колеса:', error);
      return [];
    }
  }, []);

  const initializeWheel = useCallback(async () => {
    if (!wheelContainerRef.current || isInitializedRef.current) return;

    try {
      const items = await createWheelItems();
      if (wheelInstanceRef.current) {
        wheelInstanceRef.current.remove();
      }

      wheelInstanceRef.current = new Wheel(wheelContainerRef.current, {
        items,
        itemLabelFont: 'Lato, sans-serif',
        itemLabelFontSizeMax: 24,
        itemLabelRadius: WHEEL_CONFIG.itemLabelRadius,
        lineWidth: 0,
        radius: 1,
        rotationResistance: WHEEL_CONFIG.rotationResistance,
        itemLabelAlign: 'right',
        onRest: () => onSpinComplete?.(),
        isInteractive: false,
      });

      isInitializedRef.current = true;
      onInitialized?.();
    } catch (error) {
      // console.error('Ошибка инициализации колеса:', error);
    }
  }, [createWheelItems, onSpinComplete, onInitialized]);

  useEffect(() => {
    initializeWheel();

    return () => {
      if (wheelInstanceRef.current) {
        wheelInstanceRef.current.remove();
        wheelInstanceRef.current = null;
      }
      isInitializedRef.current = false;
    };
  }, [initializeWheel]);

  const handleSpin = useCallback((targetIndex) => {
    if (!wheelInstanceRef.current) return;

    const revolutions = WHEEL_CONFIG.minRevolutions + 
      Math.floor(Math.random() * WHEEL_CONFIG.maxExtraRevolutions);
    
    wheelInstanceRef.current.spinToItem(
      targetIndex,
      WHEEL_CONFIG.duration,
      true,
      revolutions,
      1
    );

    onSpin?.(targetIndex);
    wheelInstanceRef.current.onRest = () => onSpinComplete?.(targetIndex);
  }, [onSpin, onSpinComplete]);

  useImperativeHandle(ref, () => ({
    spin: handleSpin,
    isInitialized: () => isInitializedRef.current
  }));

  return (
    <div ref={wheelContainerRef} className={styles.wheelContainer}>
      <img src={PointerIcon} alt="Pointer" className={styles.pointer} />
      <div className={styles.centerCircle}></div>
    </div>
  );
});

WheelComponent.displayName = 'WheelComponent';

export default WheelComponent;